import { useQuery } from '@tanstack/react-query';

import { getMapDevices } from './map-api';

/** Namespaced separately from `deviceKeys` (`./hooks`, the listing feature's own factory) to
 *  avoid a naming collision in this feature's shared barrel — both roots stay `['devices', ...]`. */
export const mapDeviceKeys = {
  all: ['devices'] as const,
  map: () => [...mapDeviceKeys.all, 'map'] as const,
};

/**
 * The Device marker layer's whole payload. A separate query from `useMapFields()`, which is the whole
 * of 113586 §15b/§15c: one layer erroring leaves the other rendering, and a Field is never presented
 * as having zero Devices merely because the Device layer failed.
 *
 * **The Map is a load-time snapshot, not a live surface** (113587 §1f/§1g, ADR-0016). A Device that
 * reports its first location must not appear while the Grower is watching — it lands when they reload
 * or next open the Map, independently per tab. Four settings carry that, and the app-wide defaults
 * (`staleTime: 30s`, `refetchOnWindowFocus: true`, `refetchOnReconnect: true`) violate it without
 * every one of them:
 *
 * - no `refetchInterval` — nothing arrives on a timer;
 * - `refetchOnWindowFocus: false` — otherwise merely tabbing away past the 30s staleTime and back adds
 *   a marker mid-session, which breaks §1f on its own;
 * - `refetchOnReconnect: false` — the same door, opened by the network instead of the window. It costs
 *   nothing elsewhere: `onOnline()` **also** calls `retryer.continue()`, which this flag does not gate,
 *   so a load paused by a dropped connection still completes by itself when it returns;
 * - `refetchOnMount: 'always'` — §1f's other half. `<MapPage>` is a lazy route that genuinely unmounts,
 *   so reopening the Map is a real remount and must refetch even inside the 30s staleTime.
 *
 * Per-tab independence (§1g) needs no code: the query cache is per-document.
 */
export function useMapDevices() {
  return useQuery({
    queryKey: mapDeviceKeys.map(),
    queryFn: getMapDevices,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: 'always',
  });
}
